import { Eye, ShieldCheck, Star, User, Users } from 'lucide-react';
import {
  type FormEvent,
  type ReactNode,
  useEffect,
  useRef,
  useState,
} from 'react';
import { toast } from 'sonner';
import { useAsyncData } from '../../hooks/useAsyncData';
import { usePageTitle } from '../../hooks/usePageTitle';
import { usePublisher } from '../../hooks/usePublisher';
import { useTRPC } from '../../hooks/useTRPC';
import { isLeftResult } from '../../lib/trpcTypeGuards';
import { unknownToLeft } from '../../lib/unknownToLeft';
import { cn } from '../../lib/utils';
import type { RightResult, RouterOutputs } from '../../types/TRPC';
import { getTeamManagementPermissionMessage } from '../../utils/apiKeyAccess';
import { captureAndReportError } from '../../utils/errorReporting';
import {
  canResendPublisherInvite,
  getPublisherInviteStateLabel,
  type PublisherInviteSummary,
} from '../../utils/publisherInvites';
import { Button } from '../Button';
import { Input } from '../Input';
import {
  InitialsAvatar,
  Skeleton,
  SKELETON_REVEAL_CLASS,
  SkeletonGroup,
  StatusPill,
  type StatusPillTone,
} from '../portal';
import { Select } from '../Select';
import {
  SettingsNotice,
  SettingsPage,
  SettingsRailCard,
  SettingsSection,
} from '../settings';
import { formatShortDate } from '../../utils/stringHelpers';

const ROLE_CHIPS: Record<string, { icon: ReactNode; tone: StatusPillTone }> = {
  Owner: { icon: <Star />, tone: 'waiting' },
  Admin: { icon: <ShieldCheck />, tone: 'neutral' },
  Member: { icon: <User />, tone: 'neutral' },
  Viewer: { icon: <Eye />, tone: 'neutral' },
};

function RoleChip({ role }: { role: string }) {
  const chip = ROLE_CHIPS[role] ?? ROLE_CHIPS.Member;
  return (
    <StatusPill tone={chip.tone} icon={chip.icon}>
      {role}
    </StatusPill>
  );
}

const INVITE_STATE_TONES: Record<
  PublisherInviteSummary['state'],
  StatusPillTone
> = {
  Pending: 'waiting',
  Accepted: 'published',
  Declined: 'neutral',
  Revoked: 'neutral',
  Expired: 'review',
  BlockedDeletedAccount: 'rejected',
};

/** What each role can do, mirroring `permission.service.ts#getRolePermissions`. */
const ROLE_EXPLANATIONS: { role: string; summary: string }[] = [
  { role: 'Owner', summary: 'Full access, including team management.' },
  {
    role: 'Admin',
    summary:
      'Apps, releases, NFTs and the publisher profile. Cannot manage the team.',
  },
  {
    role: 'Member',
    summary: 'Create apps, upload releases and submit to the Store.',
  },
  { role: 'Viewer', summary: 'Read-only access.' },
];

/** 20px section-title line; keeps the header row at its loaded height. */
function TitleSkeleton({ className }: { className?: string }) {
  return (
    <span className="flex h-5 items-center">
      <Skeleton className={cn('h-3.5 w-32', className)} />
    </span>
  );
}

/**
 * Mirrors the three sections below: invite form (40px controls), the dashed
 * "no pending invites" box (68px) and two 32px-avatar member rows.
 */
function TeamSkeleton() {
  return (
    <SkeletonGroup
      label="Loading team settings"
      className="flex flex-col gap-6"
    >
      <SettingsSection>
        {/* Placeholder headers stay out of the real <h2> so the loading state
            never exposes an empty heading. */}
        <TitleSkeleton />
        <div className="grid gap-4 sm:grid-cols-[minmax(0,1fr)_180px_auto]">
          <Skeleton className="h-10" rounded="lg" />
          <Skeleton className="h-10" rounded="lg" />
          <Skeleton className="h-10 w-full sm:w-[140px]" rounded="lg" />
        </div>
      </SettingsSection>

      <SettingsSection>
        <div className="flex items-center justify-between gap-4">
          <TitleSkeleton />
          <TitleSkeleton className="w-16" />
        </div>
        <div className="flex h-[68px] items-center rounded-lg border border-dashed border-seeker-700 px-4">
          <Skeleton className="h-3.5 w-44" />
        </div>
      </SettingsSection>

      <SettingsSection>
        <div className="flex items-center justify-between gap-4">
          <TitleSkeleton />
          <TitleSkeleton className="w-16" />
        </div>
        <div className="divide-y divide-seeker-800">
          {[0, 1].map((index) => (
            <div
              key={index}
              className="flex items-center gap-3 py-4 first:pt-0 last:pb-0"
            >
              <Skeleton className="size-8 shrink-0" rounded="full" />
              <div className="flex min-w-0 flex-1 flex-col">
                <div className="flex h-5 items-center">
                  <Skeleton className="h-3.5 w-40" />
                </div>
                <div className="flex h-5 items-center">
                  <Skeleton className="h-3.5 w-56" />
                </div>
              </div>
              <Skeleton className="h-5 w-20" rounded="full" />
            </div>
          ))}
        </div>
      </SettingsSection>
    </SkeletonGroup>
  );
}

type TeamMember = RightResult<
  RouterOutputs['listPublisherMembers']
>['members'][number];
type TeamInvite = RightResult<
  RouterOutputs['listPublisherPendingInvites']
>['invites'][number];

type InviteRole = 'Admin' | 'Member' | 'Viewer';
const INVITE_ROLE_CHOICES: { key: InviteRole; label: string }[] = [
  { key: 'Admin', label: 'Admin' },
  { key: 'Member', label: 'Member' },
  { key: 'Viewer', label: 'Viewer' },
];

export function TeamSettingsPage() {
  const trpc = useTRPC();
  const { activePublisher, isLoading: isPublisherLoading } = usePublisher();
  const {
    data: members,
    setData: setMembers,
    isLoading: loading,
    setIsLoading,
    run,
  } = useAsyncData<TeamMember[]>({
    initialData: [],
    initialLoading: true,
  });
  const [invites, setInvites] = useState<TeamInvite[]>([]);
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteRole, setInviteRole] = useState<InviteRole>('Member');
  const [inviting, setInviting] = useState(false);
  const [inviteActionId, setInviteActionId] = useState<string | null>(null);
  const accessError =
    !isPublisherLoading && activePublisher
      ? getTeamManagementPermissionMessage(activePublisher.canManageTeam)
      : null;

  usePageTitle('Team | Publishing Portal');

  // Only the first load shows the skeleton; reloads after invite/revoke keep the page.
  const hasLoadedRef = useRef(false);

  async function loadTeam() {
    if (!activePublisher) {
      setMembers([]);
      setInvites([]);
      setIsLoading(false);
      return;
    }

    await run(
      () =>
        Promise.all([
          trpc.listPublisherMembers.query(undefined).catch(unknownToLeft),
          trpc.listPublisherPendingInvites
            .query(undefined)
            .catch(unknownToLeft),
        ]),
      {
        showLoadingState: !hasLoadedRef.current,
        onSuccess: ([membersResult, invitesResult], helpers) => {
          hasLoadedRef.current = true;
          if (isLeftResult(membersResult)) {
            toast.error('Failed to load team settings', {
              description: membersResult.left.message,
              duration: 5000,
            });
            captureAndReportError(membersResult.left);
            return;
          }

          if (isLeftResult(invitesResult)) {
            toast.error('Failed to load team settings', {
              description: invitesResult.left.message,
              duration: 5000,
            });
            captureAndReportError(invitesResult.left);
            return;
          }

          helpers.setData(membersResult.right.members);
          setInvites(invitesResult.right.invites);
        },
      },
    );
  }

  useEffect(() => {
    if (isPublisherLoading) {
      return;
    }

    if (accessError) {
      return;
    }

    void loadTeam();
  }, [accessError, activePublisher?.publisherUserJoinId, isPublisherLoading]);

  async function handleInviteSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();

    if (!inviteEmail.trim()) {
      toast.error('Enter an email address');
      return;
    }

    setInviting(true);

    const result = await trpc.invitePublisherMember
      .mutate({
        email: inviteEmail.trim(),
        role: inviteRole,
        permissions: [],
      })
      .catch(unknownToLeft);

    setInviting(false);

    if (isLeftResult(result)) {
      toast.error('Failed to send invite', {
        description: result.left.message,
        duration: 5000,
      });
      captureAndReportError(result.left);
      return;
    }

    switch (result.right.status) {
      case 'created':
        toast.success('Invite email sent', {
          description: `An invite was sent to ${inviteEmail.trim()}.`,
          duration: 4000,
        });
        setInviteEmail('');
        break;
      case 'resent':
        toast.success('Invite email re-sent', {
          description: `A fresh invite was sent to ${inviteEmail.trim()}.`,
          duration: 4000,
        });
        setInviteEmail('');
        break;
      case 'already_member':
        toast.error('Already a team member', {
          description: `${inviteEmail.trim()} already has publisher access.`,
          duration: 5000,
        });
        break;
      case 'blocked_deleted_account':
        toast.error('Invite blocked', {
          description:
            'This email belongs to a previously removed account. Contact support to restore access before inviting it again.',
          duration: 6000,
        });
        break;
      case 'blocked_wrong_state':
        toast.error('Invite could not be sent', {
          description: 'This invite is no longer in a sendable state.',
          duration: 5000,
        });
        break;
    }

    await loadTeam();
  }

  async function handleResend(inviteId: string) {
    setInviteActionId(inviteId);

    const result = await trpc.resendPublisherInvite
      .mutate({ inviteId })
      .catch(unknownToLeft);

    setInviteActionId(null);

    if (isLeftResult(result)) {
      toast.error('Failed to resend invite', {
        description: result.left.message,
        duration: 5000,
      });
      captureAndReportError(result.left);
      return;
    }

    switch (result.right.status) {
      case 'resent':
        toast.success('Invite email re-sent');
        break;
      case 'already_member':
        toast.error('Invite no longer needed', {
          description: 'This email already has publisher access.',
          duration: 5000,
        });
        break;
      case 'blocked_deleted_account':
        toast.error('Invite blocked', {
          description:
            'This email belongs to a previously removed account. Contact support to restore access first.',
          duration: 6000,
        });
        break;
      case 'blocked_wrong_state':
        toast.error('Invite cannot be re-sent', {
          description: 'Only pending or expired invites can be re-sent.',
          duration: 5000,
        });
        break;
      case 'created':
        toast.success('Invite email sent');
        break;
    }

    await loadTeam();
  }

  async function handleRevoke(inviteId: string) {
    setInviteActionId(inviteId);

    const result = await trpc.revokePublisherInvite
      .mutate({ inviteId })
      .catch(unknownToLeft);

    setInviteActionId(null);

    if (isLeftResult(result)) {
      toast.error('Failed to revoke invite', {
        description: result.left.message,
        duration: 5000,
      });
      captureAndReportError(result.left);
      return;
    }

    toast.success('Invite revoked');
    await loadTeam();
  }

  const description = isPublisherLoading ? (
    <span className={cn('flex h-5 items-center', SKELETON_REVEAL_CLASS)}>
      <Skeleton className="h-3.5 w-full max-w-lg" />
    </span>
  ) : (
    <span className="motion-enter block">
      Invite teammates to{' '}
      {activePublisher ? (
        <span className="text-white">{activePublisher.name}</span>
      ) : (
        'your publisher'
      )}
      , review current access, and manage pending email invitations without
      changing your active publisher.
    </span>
  );

  const rail = (
    <SettingsRailCard title="Roles">
      <ul>
        {ROLE_EXPLANATIONS.map((entry) => (
          <li key={entry.role}>
            <span className="font-bold text-white">{entry.role}</span> —{' '}
            {entry.summary}
          </li>
        ))}
      </ul>
    </SettingsRailCard>
  );

  function renderBody() {
    if (isPublisherLoading) {
      return <TeamSkeleton />;
    }

    // Missing ManageTeam and missing publisher are explained where you landed;
    // this page used to redirect to Storage with an error toast instead.
    if (accessError) {
      return (
        <SettingsNotice
          className="motion-enter"
          tone="warning"
          title="Team management permission required"
          description={accessError}
        />
      );
    }

    if (!activePublisher) {
      return (
        <SettingsNotice
          className="motion-enter"
          tone="warning"
          title="No active publisher"
          description="You need an active publisher before you can manage team access."
        />
      );
    }

    if (loading) {
      return <TeamSkeleton />;
    }

    return (
      <>
        <SettingsSection className="motion-enter" title="Invite teammate">
          <form
            className="grid gap-4 sm:grid-cols-[minmax(0,1fr)_180px_auto]"
            onSubmit={(event) => void handleInviteSubmit(event)}
          >
            <Input
              type="email"
              aria-label="Teammate email"
              value={inviteEmail}
              onChange={(event) => setInviteEmail(event.target.value)}
              placeholder="teammate@example.com"
              autoComplete="email"
            />

            <Select
              name="role"
              aria-label="Role"
              choices={INVITE_ROLE_CHOICES}
              defaultValue={inviteRole}
              onChange={(choice) => {
                if (choice) setInviteRole(choice.key);
              }}
            />

            <Button
              type="submit"
              variant="primary"
              disabled={inviting}
              submitting={inviting}
              className="w-full sm:min-w-[140px]"
            >
              Send Invite
            </Button>
          </form>
        </SettingsSection>

        <SettingsSection
          className="motion-enter"
          title="Pending invites"
          action={
            <span className="text-sm text-seeker-400 tabular-nums">
              {invites.length} open
            </span>
          }
        >
          {invites.length === 0 ? (
            <div className="rounded-lg border border-dashed border-seeker-700 px-4 py-6 text-sm text-seeker-400">
              No pending team invites.
            </div>
          ) : (
            <ul className="divide-y divide-seeker-800">
              {invites.map((invite) => (
                <li
                  key={invite.id}
                  className="flex flex-col gap-4 py-4 first:pt-0 last:pb-0"
                >
                  <div className="flex flex-wrap items-start justify-between gap-3">
                    <div className="min-w-0">
                      <div
                        className="truncate text-sm font-bold text-white"
                        title={invite.invitedEmail}
                      >
                        {invite.invitedEmail}
                      </div>
                      <div className="mt-1 text-sm text-seeker-400">
                        {invite.role} access
                        {invite.invitedByName
                          ? ` • invited by ${invite.invitedByName}`
                          : ''}
                      </div>
                      <div className="mt-1 text-xs leading-4 text-seeker-400 tabular-nums">
                        Expires {formatShortDate(invite.expiresAt)}
                      </div>
                    </div>

                    <StatusPill tone={INVITE_STATE_TONES[invite.state]}>
                      {getPublisherInviteStateLabel(invite.state)}
                    </StatusPill>
                  </div>

                  <div className="flex flex-wrap gap-3">
                    <Button
                      variant="outlineMuted"
                      className="h-8 px-3"
                      disabled={
                        inviteActionId === invite.id ||
                        !canResendPublisherInvite(invite)
                      }
                      onClick={() => void handleResend(invite.id)}
                    >
                      <span className="text-xs">Re-send</span>
                    </Button>
                    <Button
                      variant="dangerOutline"
                      className="h-8 px-3"
                      disabled={inviteActionId === invite.id}
                      onClick={() => void handleRevoke(invite.id)}
                    >
                      <span className="text-xs">Revoke</span>
                    </Button>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </SettingsSection>

        <SettingsSection
          className="motion-enter"
          title="Current members"
          action={
            <span className="text-sm text-seeker-400 tabular-nums">
              {members.length} {members.length === 1 ? 'member' : 'members'}
            </span>
          }
        >
          <ul className="divide-y divide-seeker-800">
            {members.map((member) => (
              <li
                key={member.publisherUserJoinId}
                className="flex items-center gap-3 py-4 first:pt-0 last:pb-0"
              >
                <InitialsAvatar name={member.fullName} size={32} />
                <div className="min-w-0 flex-1">
                  <div
                    className="truncate text-sm font-bold text-white"
                    title={member.fullName}
                  >
                    {member.fullName}
                  </div>
                  <div
                    className="truncate text-sm text-seeker-400"
                    title={member.email}
                  >
                    {member.email}
                  </div>
                </div>

                <RoleChip role={member.isOwner ? 'Owner' : member.role} />
              </li>
            ))}
          </ul>
        </SettingsSection>
      </>
    );
  }

  return (
    <SettingsPage
      icon={<Users />}
      title="Team"
      description={description}
      rail={rail}
    >
      {renderBody()}
    </SettingsPage>
  );
}
